import { useEffect, useRef } from "react";
import { CheckCircle2, Shield, BookOpen, Users, Smartphone, TrendingUp } from "lucide-react";

const FEATURES = [
  { icon: BookOpen, title: "Curriculum-Aligned Modules", desc: "Meticulously mapped to CBSE, ICSE, IGCSE, IB, and Karnataka State Board standards." },
  { icon: TrendingUp, title: "STEM-Integrated Exposure", desc: "Real-world exposure across agriculture, aviation, and maritime science disciplines." },
  { icon: Shield, title: "Safe & Professionally Guided", desc: "Fully managed environments with certified facilitators and comprehensive safety protocols." },
  { icon: Users, title: "Scalable for Every Grade", desc: "Age-appropriate, modularized content for Nursery through Grade 12." },
  { icon: CheckCircle2, title: "Holistic Development", desc: "Building scientific curiosity, collaboration, leadership, and essential life skills." },
  { icon: Smartphone, title: "Device-Free & Supervised", desc: "Intentionally device-free for deep focus, genuine discovery, and lasting retention." },
];

export default function WhyChooseSection() {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) entry.target.classList.add("visible"); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="why-choose" data-testid="why-choose-section" ref={ref} className="reveal-section py-16 md:py-20 bg-muted/50">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-accent mb-3">Why Choose Us</p>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground mb-4">
            Education That Endures
          </h2>
          <p className="text-base text-muted-foreground font-light max-w-2xl mx-auto">
            In an era of information saturation, we offer transformational learning journeys-not just field trips.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-0 border border-border/60">
          {FEATURES.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div key={i} data-testid={`feature-${i}`} className="p-8 border-b border-r border-border/60 hover:bg-card transition-colors">
                <Icon className="w-5 h-5 text-accent mb-4" />
                <h4 className="font-heading text-lg font-medium text-foreground mb-2">{feature.title}</h4>
                <p className="text-sm text-muted-foreground font-light leading-relaxed">{feature.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
